/**
 * Trip logging.
 *
 * The old log kept a breadcrumb every 40 m and stored three numbers per point,
 * which was enough to draw a line and nothing else - you couldn't tell how
 * fast you drove, where you sat in traffic, or whether the route beat its
 * estimate. It now records:
 *
 *   - a full track with speed, heading and altitude per sample,
 *   - a segment rollup every ~2.5 minutes (avg/max speed, distance, time
 *     stopped, and the road number you were on), which is what makes the
 *     history readable and what the AI advisor compares against,
 *   - departure predictions, so "did this route deliver?" is answerable.
 *
 * Sampling is adaptive: dense enough at speed to reconstruct the line, sparse
 * when parked so an hour at a loading bay doesn't fill the store.
 */

import type { Fix, LngLat, Trip, TripSegment, TrackPoint } from '../core/types';
import { distance } from '../core/geo';
import type { VehicleProfile } from '../core/settings';

/** Emit a segment roughly this often. */
const SEGMENT_SECONDS = 150;
/** Below this speed we count the driver as stopped. km/h. */
const STOPPED_KMH = 3;
/** A jump further than this between fixes is a GPS teleport, not driving. */
const TELEPORT_METRES = 400;

export interface TripStart {
  destinationName: string;
  destination: LngLat;
  originName: string;
  origin: LngLat | null;
  vehicle: VehicleProfile;
  predictedDuration: number;
  predictedDistance: number;
  trafficSignals: number;
}

export class TripRecorder {
  private trip: Trip;
  private last: { p: LngLat; t: number } | null = null;
  private pendingRoughness: number | null = null;
  private segStart = 0;
  private segDistance = 0;
  private segSpeedSum = 0;
  private segSpeedCount = 0;
  private segMax = 0;
  private segStopped = 0;
  private segRoads = new Map<string, number>();
  private segCenter: LngLat | null = null;
  private segRoughSum = 0;
  private segRoughCount = 0;
  private roughSum = 0;
  private roughCount = 0;
  private roughMax = 0;
  private movingMs = 0;
  private maxSpeed = 0;
  private speedSum = 0;
  private speedCount = 0;

  constructor(start: TripStart, now = Date.now()) {
    this.trip = {
      id: crypto.randomUUID(),
      startedAt: now,
      endedAt: 0,
      originName: start.originName,
      origin: start.origin ?? undefined,
      destinationName: start.destinationName,
      destination: start.destination,
      distance: 0,
      duration: 0,
      vehicleId: start.vehicle.name,
      trafficSignals: start.trafficSignals,
      predictedDuration: start.predictedDuration,
      predictedDistance: start.predictedDistance,
      reroutes: 0,
      segments: [],
      track: [],
    };
    this.segStart = now;
  }

  get id(): string {
    return this.trip.id;
  }

  get distance(): number {
    return this.trip.distance;
  }

  noteReroute(): void {
    this.trip.reroutes = (this.trip.reroutes ?? 0) + 1;
  }

  /**
   * Feed a GPS fix. `road` is the route number or readable name we're on, used
   * to label the segment.
   */
  add(
    fix: Fix,
    road: string,
    tier?: 'motorway' | 'dual' | 'single',
    roughness?: number | null,
  ): void {
    this.pendingRoughness = roughness ?? null;
    const kmh = fix.speed != null ? Math.max(0, fix.speed * 3.6) : 0;
    const here: LngLat = [fix.lng, fix.lat];

    if (!this.last) {
      this.last = { p: here, t: fix.timestamp };
      this.segCenter = here;
      this.push(fix, kmh);
      return;
    }

    const moved = distance(this.last.p, here);
    const dtMs = Math.max(0, fix.timestamp - this.last.t);

    // Sample densely enough to redraw the road, but don't log a stationary
    // vehicle 3 000 times while it waits at a depot.
    const movingFast = kmh > 15;
    const minMetres = movingFast ? 25 : 12;
    const minMs = kmh < STOPPED_KMH ? 30_000 : 5_000;
    const shouldSample = moved >= minMetres || dtMs >= minMs;
    if (!shouldSample) return;

    if (moved < TELEPORT_METRES) {
      this.trip.distance += moved;
      this.segDistance += moved;
      if (kmh >= STOPPED_KMH) this.movingMs += dtMs;
      else this.segStopped += dtMs / 1000;
    }

    if (kmh > this.maxSpeed) this.maxSpeed = kmh;
    if (kmh > this.segMax) this.segMax = kmh;
    if (kmh >= STOPPED_KMH) {
      this.speedSum += kmh;
      this.speedCount++;
      this.segSpeedSum += kmh;
      this.segSpeedCount++;
    }
    if (road) this.segRoads.set(road, (this.segRoads.get(road) ?? 0) + 1);

    // Learn the cruising speed actually achieved on this class of road.
    // Only while genuinely moving - counting a red light as "0 on a
    // motorway" would teach the ETA model nonsense.
    if (tier && kmh >= 25) {
      const t = this.trip.speedByTier ?? (this.trip.speedByTier = {});
      const e = t[tier] ?? (t[tier] = { n: 0, sum: 0, max: 0 });
      e.n++;
      e.sum += kmh;
      if (kmh > e.max) e.max = kmh;
    }
    if (this.pendingRoughness != null) {
      this.segRoughSum += this.pendingRoughness;
      this.segRoughCount++;
      this.roughSum += this.pendingRoughness;
      this.roughCount++;
      if (this.pendingRoughness > this.roughMax) this.roughMax = this.pendingRoughness;
    }
    this.segCenter = here;

    this.push(fix, kmh);
    this.last = { p: here, t: fix.timestamp };

    if (fix.timestamp - this.segStart >= SEGMENT_SECONDS * 1000) {
      this.closeSegment(fix.timestamp);
    }
  }

  private push(fix: Fix, kmh: number): void {
    const point: TrackPoint = [
      round(fix.lng, 5),
      round(fix.lat, 5),
      fix.timestamp,
      Math.round(kmh),
      fix.heading == null ? -1 : Math.round(fix.heading),
      fix.altitude == null ? 0 : Math.round(fix.altitude),
      this.pendingRoughness == null ? -1 : Math.round(this.pendingRoughness),
    ];
    this.trip.track.push(point);
    // Hard cap: a very long drive must not grow without bound. Beyond this we
    // thin the oldest half rather than dropping the newest data.
    if (this.trip.track.length > 6000) {
      this.trip.track = this.trip.track.filter((_, i) => i % 2 === 0 || i > 3000);
    }
  }

  private closeSegment(at: number): void {
    if (this.segDistance < 5 && this.segStopped < 30) {
      this.segStart = at;
      return;
    }
    let road = '';
    let best = 0;
    for (const [name, n] of this.segRoads) {
      if (n > best) {
        best = n;
        road = name;
      }
    }

    const segment: TripSegment = {
      startedAt: this.segStart,
      endedAt: at,
      distance: Math.round(this.segDistance),
      avgSpeed: this.segSpeedCount ? Math.round(this.segSpeedSum / this.segSpeedCount) : 0,
      maxSpeed: Math.round(this.segMax),
      stoppedSeconds: Math.round(this.segStopped),
      road,
      center: this.segCenter ?? [0, 0],
      roughness: this.segRoughCount
        ? Math.round(this.segRoughSum / this.segRoughCount)
        : undefined,
    };
    this.trip.segments!.push(segment);

    this.segStart = at;
    this.segDistance = 0;
    this.segSpeedSum = 0;
    this.segSpeedCount = 0;
    this.segMax = 0;
    this.segStopped = 0;
    this.segRoughSum = 0;
    this.segRoughCount = 0;
    this.segRoads.clear();
  }

  /** Close the log. Returns null when the drive was too short to be worth keeping. */
  finish(vehicle: VehicleProfile, now = Date.now()): Trip | null {
    this.closeSegment(now);
    const t = this.trip;
    t.endedAt = now;
    t.duration = Math.round((now - t.startedAt) / 1000);
    t.movingSeconds = Math.round(this.movingMs / 1000);
    t.maxSpeed = Math.round(this.maxSpeed);
    t.avgSpeed = this.speedCount ? Math.round(this.speedSum / this.speedCount) : 0;
    if (this.roughCount > 0) {
      t.roughness = {
        avg: Math.round(this.roughSum / this.roughCount),
        max: Math.round(this.roughMax),
      };
    }
    if (vehicle.consumption) {
      t.fuelEstimate = round((t.distance / 1000) * (vehicle.consumption / 100), 2);
    }
    return t.distance > 200 ? t : null;
  }
}

function round(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/** Normalise a stored point to the 6-tuple shape, tolerating legacy 3-tuples. */
export function readPoint(p: readonly number[]): TrackPoint {
  // Three log generations coexist in storage: [lng,lat,t], that plus speed /
  // heading / altitude, and the current tuple with roughness on the end.
  const [lng = 0, lat = 0, t = 0, speed = 0, heading = -1, altitude = 0, rough = -1] = p;
  return [lng, lat, t, speed, heading, altitude, rough];
}
